import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@core/ui/cn';
import type { Row } from '@core/store/model';
import { dateRange, daysBetween, monthYearLabel, type ISODate } from '@core/dates';
import { Tooltip } from '@core/ui/Tooltip';
import { assignSubRows } from '../shared/geometry';
import type { Attention, Task } from '../shared/data';
import { eventPillLook } from '../shared/ContextBand';
import { laneMonths, laneReleases, monthTint, weeksOf, type Lane, type Release, type Week } from './planner';
import { Comb } from './TimelineChips';
import type { CardActions, Person, ProjectInfo } from './ReleaseCard';
import { Menu } from '@core/ui/Menu';
import { SquarePen } from 'lucide-react';

type Event = Row<'event'>;

const MIN_WEEK = 84;      // уже — чипы не читаются
const FIT_WEEKS = 13;     // ширина недели — от трёх месяцев в ширину экрана; длиннее — прокрутка, не ужимание
const LANE_PAD = 24;
const EVENT_ROW = 20;

type Props = {
  lanes: Lane[];
  releases: Release[];
  events: Event[];
  range: { from: ISODate; to: ISODate };
  today: ISODate;
  projectOf: (t: Task) => ProjectInfo;
  peopleOf: (t: Task) => Person[];
  attention: Map<string, Attention>;
  onOpen: (r: Release) => void;
  onOpenEvent: (e: Event) => void;
  actions: CardActions;
  onDrop: (r: Release, monday: ISODate) => void;
};

/**
 * Timeline: ось недель, лента на блок — имя, полоса месяцев (и событий как контекста), чипы над и под
 * осью по неделям, чипы тянутся между неделями. Каждая лента самодостаточна: их снимают по одной.
 */
export function Timeline({ lanes, releases, events, range, today, projectOf, peopleOf, attention, actions, onOpen, onOpenEvent, onDrop }: Props) {
  const weeks = weeksOf(range);
  const root = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const weekWidth = Math.max(MIN_WEEK, Math.floor((width - LANE_PAD * 2) / FIT_WEEKS));
  const shown = lanes.map((lane) => ({ lane, releases: laneReleases(lane, releases) })).filter((x) => x.releases.length > 0);
  return (
    <div ref={root} className="flex flex-col gap-6" data-timeline data-week-width={weekWidth}>
      {shown.length === 0 && <p className="text-ink-muted">No releases in this range.</p>}
      {shown.map(({ lane, releases: laneList }) => (
        <LaneBlock
          key={lane.id} lane={lane} releases={laneList} events={lane.showEvents ? events : []} weeks={weeks} weekWidth={weekWidth} today={today}
          range={range} projectOf={projectOf} peopleOf={peopleOf} attention={attention} actions={actions} onOpen={onOpen} onOpenEvent={onOpenEvent} onDrop={onDrop}
        />
      ))}
    </div>
  );
}

type LaneProps = Omit<Props, 'lanes'> & { lane: Lane; weeks: Week[]; weekWidth: number };

function LaneBlock({ lane, releases, events, weeks, weekWidth, today, range, projectOf, peopleOf, attention, actions, onOpen, onOpenEvent, onDrop }: LaneProps) {
  const total = weeks.length * weekWidth;
  const { months, byWeek } = laneMonths(releases, range);
  const dayWidth = weekWidth / 7;
  const eventRows = assignSubRows(events);
  const eventRowCount = events.length ? Math.max(...[...eventRows.values()]) + 1 : 0;
  return (
    <section data-lane={lane.id} className="rounded-xl border border-gray-300 bg-white shadow-md">
      <h3 className="px-4 pt-3 pb-1 font-bold text-sm uppercase tracking-wide text-gray-700">
        {lane.name} <span className="font-normal normal-case text-gray-500">· {releases.length} {releases.length === 1 ? 'release' : 'releases'}</span>
      </h3>
      <div className="px-4 pb-3 overflow-x-auto scroller" data-lane-scroll>
        <div className="relative" style={{ width: total }}>
          {/* полоса месяцев со счётчиками */}
          <div className="relative flex" style={{ height: 32 }}>
            {months.map((m) => (
              <div key={m.month} className="h-full flex items-center px-3 rounded-md mr-0.5 last:mr-0 text-sm font-bold" style={{ width: m.weeks * weekWidth - 2, background: monthTint(m.month).bg, color: monthTint(m.month).fg }}>
                {monthYearLabel(m.month)}
                <span className="ml-2 font-normal opacity-70">· {m.releases} {m.releases === 1 ? 'release' : 'releases'}</span>
              </div>
            ))}
          </div>
          {/* события — контекст на весь период, полупрозрачно, только если у ленты включены */}
          {eventRowCount > 0 && (
            <div className="relative mt-1" style={{ height: eventRowCount * EVENT_ROW }} data-lane-events>
              {events.map((e) => {
                const from = Math.max(0, daysBetween(range.from, e.start_date));
                const to = Math.min(weeks.length * 7, daysBetween(range.from, e.end_date) + 1);
                if (to <= from) return null;
                const look = eventPillLook(e);
                return (
                  <Menu key={e.id} items={[{ label: 'Edit event', onSelect: () => onOpenEvent(e), icon: SquarePen }]}>
                  <Tooltip title={e.name} lines={[dateRange(e.start_date, e.end_date), ...(look.tentative ? [{ text: 'Part of a series, not confirmed yet', tone: 'warn' as const }] : [])]} hint="Double-click to edit">
                  <button
                    type="button" data-timeline-event={e.id} data-tentative={look.tentative || undefined}
                    className={cn('min-h-0 absolute h-4 rounded text-[11px] font-semibold px-1 truncate text-left opacity-80 hover:opacity-100', look.className, look.tentative ? 'leading-3' : 'leading-4')}
                    style={{ left: from * dayWidth, width: (to - from) * dayWidth, top: (eventRows.get(e.id) ?? 0) * EVENT_ROW + 2, ...look.style }}
                    onDoubleClick={() => onOpenEvent(e)}
                  >{e.name}</button>
                  </Tooltip>
                  </Menu>
                );
              })}
            </div>
          )}
          <Comb weeks={weeks} weekWidth={weekWidth} byWeek={byWeek} today={today} projectOf={projectOf} peopleOf={peopleOf} attention={attention} actions={actions} onOpen={onOpen} onDrop={onDrop} />
        </div>
      </div>
    </section>
  );
}
